import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useRecoilState } from 'recoil';

import StandardModal from '@/components/commons/StandardModal';
import { UPDATE_USER, updateBriefDirect } from '@/components/onboarding/Actions';
import { userAtom } from '@/components/with_auth/State';

import PersonalizePanel from './PersonalizePanel';
import CareerHistoryPanel from './CareerHistoryPanel';
import ManualJobHistoryPanel from './ManualJobHistoryPanel';
import SelectImportMethodPanel from './SelectImportMethodPanel';
import SelectStarterBioPanel from './SelectStarterBioPanel';
import ReviseBioPanel from './ReviseBioPanel';
import EditBioPanel from './EditBioPanel';
import CommunicationPreferencesPanel from './CommunicationPreferencesPanel';
import MeetingPreferencesPanel from './MeetingPreferencesPanel';
import SocialLinksPanel from './SocialLinksPanel';
import ReviewYourBioPanel from './ReviewYourBioPanel';
import SharePanel from './SharePanel';

// we're using any here to avoid a proptype error!
// it should be deleted as soon as we fully embrace typescript
interface Props {
    readonly step: number,
    readonly isEdit: boolean,
    readonly closeModal: () => void,
    readonly display: boolean,
    readonly openBrief: any;
    readonly openModal: () => void,
}

const PERSONALIZE = 'personalize';
const CAREERHISTORY = 'career-history';
const MANUAL = 'manual-history';
const IMPORT = 'select-import-method';
const STARTERBIO = 'select-starter-bio';
const REVISE = 'revise-bio';
const EDIT = 'edit-bio';
const COMMPREF = 'communication-preferences';
const MEETPREF = 'meeting-preferences';
const SOCIAL = 'social-links';
const REVIEW = 'review-your-bio';
const SHARE = 'complete-share';

const STEPS = [PERSONALIZE, CAREERHISTORY, MANUAL, IMPORT, STARTERBIO,
    REVISE, EDIT, COMMPREF, MEETPREF, SOCIAL, REVIEW, SHARE];

function BioModal({
    step,
    isEdit,
    closeModal,
    display,
    openBrief,
    openModal,
}: Props) {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [previousStepIndex, setPreviousStepIndex] = useState(null);
    const currentViewName = STEPS[currentStepIndex];

    const [user, setUser] = useRecoilState(userAtom);

    const [updateUser] = useMutation(UPDATE_USER);

    useEffect(() => {
        setCurrentStepIndex(step);
    }, [step]);

    const handleContinue = async (selection = '', review = false) => {
        // TODO(tom) Revisit, idk if this is doing anything review never seems set
        if (review && !openBrief.open) {
            // go back to brief review in modal, dont close it
            setCurrentStepIndex(10);
            return;
        }
        if (isEdit) { closeModal(); return; }
        setPreviousStepIndex(currentStepIndex);
        if ((currentViewName === CAREERHISTORY && selection === 'import')
            || currentViewName === MANUAL
            || (currentViewName === STARTERBIO && selection === 'skip')) {
            // if on career history step and the user selects import
            // or current view is manual-history
            // skip one step
            setCurrentStepIndex((prevState) => prevState + 2);
        } else if (selection === 'skip') {
            let offset = 5;
            if (currentViewName === IMPORT) {
                offset = 3;
            }
            // send to edit bio
            setCurrentStepIndex((prevState) => prevState + offset);
            // update brief to be skipped
            updateBriefDirect(
                openBrief.briefIndex,
                { skippedBioGeneration: true },
                { user, setUser },
            );
        } else if (currentStepIndex === STEPS.length - 1) {
            // at the end, update user, close modal
            const attributes = {
                hasCreatedBio: true,
            };

            await updateUser({
                variables: {
                    updateUserInput: {
                        attributes,
                    },
                },
            });
            closeModal();
        } else {
            // default behavior
            setCurrentStepIndex((prevState) => prevState + 1);
        }
    };

    const handleBack = (skipped: boolean = false) => {
        if (skipped) {
            setCurrentStepIndex(previousStepIndex);
            return;
        }
        if (previousStepIndex === null || previousStepIndex === 0) {
            // on landing page, close modal
            closeModal();
        } else {
            setPreviousStepIndex(currentStepIndex);
            let offset = 1;
            if (currentStepIndex === 3) { // import file(s)
                offset = 2;
            }

            setCurrentStepIndex(currentStepIndex - offset);
        }
    };

    const defaultProps = {
        extendContinue: handleContinue,
        overrideBack: handleBack,
        isModal: true,
        isEdit,
        closeModal,
        openBrief,
        openModal,
    };

    return (
        <StandardModal
            open={display}
            onClose={closeModal}
            data-testid="modal-bio"
            headline=""
            classNames={{
                modal: 'flex w-full max-w-[1200px] mx-auto focus:outline-none focus-visible:outline-none focus-visible:ring-0',
            }}
            styles={{
                modalContainer: {
                    // NOTE(tom) for now this will remove the centering of the modal but
                    // also allow the whole modal to scroll and accommodate smaller screen sizes
                    // but also use all of the size on larger screens if available.
                    display: 'block',
                    // maxWidth: '100%',
                    // justifyContent: 'center',
                    // alignContent: 'center',
                    // alignItems: 'center',
                },
            }}
        >
            <div className="flex flex-col justify-center items-center">
                {currentViewName === PERSONALIZE && <PersonalizePanel {...defaultProps} />}
                {currentViewName === CAREERHISTORY && (
                    <CareerHistoryPanel
                        isModal
                        extendContinue={handleContinue}
                        isEdit={isEdit}
                        openBrief={openBrief}
                        openModal={openModal}
                    />
                )}
                {currentViewName === MANUAL && <ManualJobHistoryPanel {...defaultProps} />}
                {currentViewName === IMPORT && <SelectImportMethodPanel {...defaultProps} />}
                {currentViewName === STARTERBIO && <SelectStarterBioPanel {...defaultProps} />}
                {currentViewName === REVISE && <ReviseBioPanel {...defaultProps} />}
                {currentViewName === EDIT && <EditBioPanel {...defaultProps} />}
                {currentViewName === COMMPREF
                    && <CommunicationPreferencesPanel {...defaultProps} />}
                {currentViewName === MEETPREF && <MeetingPreferencesPanel {...defaultProps} />}
                {currentViewName === SOCIAL && <SocialLinksPanel {...defaultProps} />}
                {currentViewName === REVIEW && <ReviewYourBioPanel {...defaultProps} />}
                {currentViewName === SHARE && <SharePanel {...defaultProps} />}
            </div>
        </StandardModal>
    );
}

export default BioModal;
